import React from 'react';
import { Link } from 'react-router-dom';

/**
 * PageBlocks - Rendu des blocs d'une page éditoriale
 *
 * Le corps d'une page est un tableau de blocs typés, pas du HTML de mise en
 * page. Chaque type de bloc a son composant : la mise en forme vit ici, dans
 * du JSX, et non dans du balisage stocké en base.
 *
 * Ce choix vient d'une mesure faite sur le serveur : getSafeHTML retire
 * style=, class= et data-type=, et supprime <table> et <img>. Du HTML de mise
 * en page serait donc aplati à l'enregistrement. En le remplaçant par des
 * blocs, la charte reste tenue par les composants et un administrateur ne
 * peut pas la casser.
 *
 * Ajouter un type de bloc suppose de l'ajouter AUSSI à BLOC_TYPES dans
 * backend/cfc/cabaza_pages.cfc, sans quoi il sera refusé à l'écriture.
 */

import TarifBloc from './blocs/TarifBloc';
import CapturesBloc from './blocs/CapturesBloc';
import FaqBloc from './blocs/FaqBloc';
import ComparatifBloc from './blocs/ComparatifBloc';
import ArgumentsBloc from './blocs/ArgumentsBloc';
import BandeauCtaBloc from './blocs/BandeauCtaBloc';
import EtapesBloc from './blocs/EtapesBloc';
import ChiffresBloc from './blocs/ChiffresBloc';
import TemoignagesBloc from './blocs/TemoignagesBloc';
import GrilleModulesBloc from './blocs/GrilleModulesBloc';
import ModuleLieBloc from './blocs/ModuleLieBloc';
import OngletsBloc from './blocs/OngletsBloc';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

/**
 * Rend un champ de texte riche.
 *
 * dangerouslySetInnerHTML se limite désormais à CE SEUL cas : un champ
 * `contenu` déjà passé par getSafeHTML à l'écriture (cabaza_pages.cfc,
 * SanitizeRichText), qui n'y laisse survivre que <p>, <strong>, <em>, <ul>,
 * <li>, <h*> sans attribut et <a href> relatif ou https:. Ce n'est plus une
 * page entière qui est injectée, mais un fragment de texte courant — la
 * surface est bien plus étroite qu'avec du HTML de mise en page.
 *
 * Ne pas reproduire ce motif sur du contenu qui n'aurait pas traversé
 * getSafeHTML à l'écriture.
 */
const RichText = ({ html, className }) => {
    if (!html) {
        return null;
    }

    return (
        <div
            className={className}
            data-testid="bloc-richtext"
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

/**
 * Le hero d'une page de présentation.
 *
 * Quatre ajouts FACULTATIFS par rapport à la version d'origine : un badge,
 * un sous-titre en dégradé, et deux actions. Sans eux, le rendu est
 * exactement celui d'avant — les 21 pages déjà en base ne bougent pas.
 *
 * Le sous-titre passe en dégradé dès qu'une action est présente : c'est le
 * signe qu'on est sur une page qui vend, et non sur une simple page de
 * documentation. Le vocabulaire vient de LandingPage.
 */
const HeroBloc = ({ bloc }) => {
    const aUneAction = Boolean(bloc.actionTexte && bloc.actionLien);
    const aUneSecondaire = Boolean(bloc.actionSecondaireTexte && bloc.actionSecondaireLien);

    return (
        <section className="mb-10" data-testid="bloc-hero">
            {bloc.image && (
                <img
                    src={`${FILES_BASE_URL}/${bloc.image}`}
                    alt=""
                    className="w-full h-56 md:h-72 object-cover rounded-2xl mb-6"
                />
            )}

            {bloc.badge && (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 border border-green-200 mb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-600" />
                    <span className="text-xs font-semibold text-green-700">{bloc.badge}</span>
                </div>
            )}

            {bloc.titre && (
                <h2 className={`font-bold text-gray-900 mb-2 ${aUneAction ? 'text-3xl md:text-4xl font-extrabold leading-tight' : 'text-2xl md:text-3xl'}`}>
                    {bloc.titre}
                </h2>
            )}

            {bloc.sousTitre && (
                <p className={aUneAction
                    ? 'text-lg md:text-xl font-semibold bg-gradient-to-r from-cabaza-purple to-cabaza-pink bg-clip-text text-transparent max-w-2xl'
                    : 'text-lg text-gray-500'}>
                    {bloc.sousTitre}
                </p>
            )}

            {(aUneAction || aUneSecondaire) && (
                <div className="flex flex-wrap gap-3 mt-6">
                    {aUneAction && (
                        <Link
                            to={bloc.actionLien}
                            className="px-6 py-3 rounded-xl bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white font-semibold hover:opacity-90 transition-opacity"
                        >
                            {bloc.actionTexte}
                        </Link>
                    )}
                    {aUneSecondaire && (
                        <Link
                            to={bloc.actionSecondaireLien}
                            className="px-5 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:border-gray-300 transition-colors"
                        >
                            {bloc.actionSecondaireTexte}
                        </Link>
                    )}
                </div>
            )}
        </section>
    );
};

const TexteBloc = ({ bloc }) => (
    <section className="mb-8" data-testid="bloc-texte">
        <RichText html={bloc.contenu} className="prose prose-purple max-w-none text-gray-600" />
    </section>
);

/**
 * Les tons d'un encadré — une LISTE FERMÉE, jamais une couleur saisie.
 *
 * Le ton vient de la base, donc d'un champ éditable. Laisser passer une
 * couleur libre aurait deux conséquences : la cohérence visuelle du site
 * partirait page par page, et la valeur atterrirait telle quelle dans un
 * nom de classe — une chaîne arbitraire venue de la base dans du CSS.
 *
 * Les classes sont écrites EN TOUTES LETTRES : Tailwind analyse le source
 * à la compilation, et une classe construite par concaténation
 * (`bg-${ton}-50`) serait absente du CSS produit.
 */
const TONS_ENCADRE = {
    neutre:  { fond: 'bg-purple-50 border-purple-100', titre: 'text-purple-900', texte: 'prose-purple text-purple-800' },
    alerte:  { fond: 'bg-red-50 border-red-200',       titre: 'text-red-900',    texte: 'prose-red text-red-800' },
    succes:  { fond: 'bg-green-50 border-green-200',   titre: 'text-green-900',  texte: 'prose-green text-green-800' },
    info:    { fond: 'bg-blue-50 border-blue-200',     titre: 'text-blue-900',   texte: 'prose-blue text-blue-800' }
};

const EncadreBloc = ({ bloc }) => {
    // Un ton inconnu retombe sur neutre : la valeur n'est jamais employée
    // pour fabriquer une classe.
    const ton = TONS_ENCADRE[bloc.ton] || TONS_ENCADRE.neutre;

    return (
        <aside
            className={`mb-8 px-5 py-4 rounded-xl border ${ton.fond}`}
            data-testid="bloc-encadre"
        >
            {bloc.titre && (
                <h3 className={`font-semibold mb-2 ${ton.titre}`}>{bloc.titre}</h3>
            )}
            <RichText html={bloc.contenu} className={`prose max-w-none text-sm ${ton.texte}`} />
        </aside>
    );
};

const BoutonBloc = ({ bloc }) => {
    if (!bloc.href || !bloc.libelle) {
        return null;
    }

    const className = 'inline-flex items-center px-5 py-2.5 rounded-xl bg-gradient-to-br from-cabaza-purple to-cabaza-pink text-white font-medium hover:opacity-90 transition-opacity';

    // Un lien interne passe par le routeur pour éviter un rechargement complet ;
    // un lien externe (https:, mailto:) reste une ancre classique. Les href ont
    // été restreints au relatif, https: et mailto: à l'écriture.
    const isInternal = bloc.href.startsWith('/');

    return (
        <div className="mb-8" data-testid="bloc-bouton">
            {isInternal ? (
                <Link to={bloc.href} className={className}>{bloc.libelle}</Link>
            ) : (
                <a href={bloc.href} className={className} rel="noopener noreferrer">
                    {bloc.libelle}
                </a>
            )}
        </div>
    );
};

const GrilleCartesBloc = ({ bloc }) => {
    const cartes = Array.isArray(bloc.cartes) ? bloc.cartes : [];

    if (cartes.length === 0) {
        return null;
    }

    return (
        <section className="mb-8 grid gap-4 sm:grid-cols-2" data-testid="bloc-grilleCartes">
            {cartes.map((carte, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-4">
                    {carte.titre && (
                        <h3 className="font-semibold text-gray-900 mb-1">{carte.titre}</h3>
                    )}
                    <RichText html={carte.texte} className="prose prose-sm prose-purple max-w-none text-gray-600" />
                </div>
            ))}
        </section>
    );
};

const BLOC_COMPONENTS = {
    hero: HeroBloc,
    texte: TexteBloc,
    encadre: EncadreBloc,
    bouton: BoutonBloc,
    grilleCartes: GrilleCartesBloc,
    tarif: TarifBloc,
    captures: CapturesBloc,
    faq: FaqBloc,
    comparatif: ComparatifBloc,
    arguments: ArgumentsBloc,
    bandeauCta: BandeauCtaBloc,
    etapes: EtapesBloc,
    onglets: OngletsBloc,
    chiffres: ChiffresBloc,
    temoignages: TemoignagesBloc,
    // Le seul bloc qui lit la base au lieu de porter son contenu : les 21
    // modules recopies deviendraient faux des qu'on en ajoute un.
    grilleModules: GrilleModulesBloc,
    // La suggestion editoriale d'un module complementaire : le choix est
    // humain, mais le nom et le resume restent lus en base.
    moduleLie: ModuleLieBloc
};

/**
 * Rend la suite des blocs d'une page.
 *
 * Un bloc de type inconnu est ignoré silencieusement plutôt que de faire
 * échouer la page entière : le backend refuse déjà ces blocs à l'écriture,
 * donc un type inconnu ici signale une base plus récente que le frontend
 * (déploiement partiel), pas une donnée hostile.
 *
 * @param {Array} blocs - Tableau de blocs typés
 */
const PageBlocks = ({ blocs, langue = 'fr' }) => {
    if (!Array.isArray(blocs) || blocs.length === 0) {
        return null;
    }

    return (
        <>
            {blocs.map((bloc, index) => {
                const Composant = BLOC_COMPONENTS[bloc?.type];

                if (!Composant) {
                    return null;
                }

                // La langue sert au bloc tarif : le formatage monetaire
                // en depend, et c'est la seule donnee qu'il ne lit pas en base.
                return <Composant key={index} bloc={bloc} langue={langue} />;
            })}
        </>
    );
};

export default PageBlocks;
